import { UUID_REGEX } from '@/constants/chat';
import { api } from '../core';
import type { GetDraftVersionResponse } from './getDraftVersion';

export interface GetDraftCitationIdsRequest {
  draftVersionIds: string[];
}

/** `draftVersionId -> citation ids`, each in the order the draft first cites them. */
export type GetDraftCitationIdsResponse = Record<string, string[]>;

const citationIdsInOrder = (content: string): string[] => {
  const ids = Array.from(content.matchAll(new RegExp(UUID_REGEX.source, UUID_REGEX.flags)), ([, id]) => id);
  return Array.from(new Set(ids));
};

const getDraftCitationIdsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /** Reads each draft with an empty `citationIndexMap`, which keeps its raw `{{citationId}}` markers. */
    getDraftCitationIds: builder.query<GetDraftCitationIdsResponse, GetDraftCitationIdsRequest>({
      queryFn: async ({ draftVersionIds }, _api, _extraOptions, baseQuery) => {
        const results = await Promise.all(
          draftVersionIds.map((draftVersionId) =>
            baseQuery({
              url: '/chat/draft/get-version',
              method: 'POST',
              body: { draftVersionId, citationIndexMap: {} },
            }),
          ),
        );

        const failed = results.find((result) => result.error);
        if (failed?.error) return { error: failed.error };

        const data: GetDraftCitationIdsResponse = {};
        draftVersionIds.forEach((draftVersionId, index) => {
          data[draftVersionId] = citationIdsInOrder((results[index].data as GetDraftVersionResponse).content ?? '');
        });
        return { data };
      },
    }),
  }),
});

export const { useGetDraftCitationIdsQuery, useLazyGetDraftCitationIdsQuery } = getDraftCitationIdsApi;
